// client/src/components/settings/TeamMembers.js
// TEAM MEMBERS MANAGEMENT - Redesigned with consistent professional layout
// Matches CustomerDashboard.js and CustomerList.js design patterns

import React, { useState, useEffect } from 'react';
import apiClient from '../../services/api';

export default function TeamMembers({ businessData }) {
  const [teamMembers, setTeamMembers] = useState([]);
  const [currentUserIsOwner, setCurrentUserIsOwner] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [generatedPassword, setGeneratedPassword] = useState('');
  const [showInviteForm, setShowInviteForm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    loadTeamMembers();
  }, []);

  const loadTeamMembers = async () => {
    try {
      setLoading(true);
      const response = await apiClient.get('/team/members');

      console.log('Team members API response:', response.data);

      if (response.data.success) {
        setTeamMembers(response.data.members);

        // Get current user's email from localStorage
        let currentUserEmail = null;
        const storedUser = localStorage.getItem('user');
        if (storedUser) {
          try {
            const userData = JSON.parse(storedUser);
            currentUserEmail = userData.email;
          } catch (e) {
            console.error('Failed to parse user data:', e);
          }
        }

        console.log('Current user email:', currentUserEmail);

        const currentMember = response.data.members.find(m => m.email === currentUserEmail);
        console.log('Current member data:', currentMember);

        if (currentMember) {
          console.log('Setting currentUserIsOwner to:', currentMember.isOwner);
          setCurrentUserIsOwner(currentMember.isOwner);
        } else {
          console.log('Current user not found in team members, defaulting to false');
          setCurrentUserIsOwner(false);
        }
      }
    } catch (error) {
      console.error('Error loading team members:', error);

      // Get current user email for fallback
      let currentUserEmail = 'owner@business.com';
      const storedUser = localStorage.getItem('user');
      if (storedUser) {
        try {
          const userData = JSON.parse(storedUser);
          currentUserEmail = userData.email;
        } catch (e) {
          console.error('Failed to parse user data:', e);
        }
      }

      // Show current user as owner if API fails
      setTeamMembers([
        {
          id: '1',
          name: businessData?.businessName || 'Business Owner',
          email: currentUserEmail,
          role: 'admin',
          status: 'active',
          joinedAt: new Date().toISOString(),
          isOwner: true
        }
      ]);
      setCurrentUserIsOwner(true);
    } finally {
      setLoading(false);
    }
  };

  const generatePassword = () => {
    const length = 12;
    const charset = 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789!@#$%^&*';
    let password = '';

    // Ensure at least one of each type
    password += 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'[Math.floor(Math.random() * 26)];
    password += 'abcdefghijklmnopqrstuvwxyz'[Math.floor(Math.random() * 26)];
    password += '0123456789'[Math.floor(Math.random() * 10)];
    password += '!@#$%^&*'[Math.floor(Math.random() * 8)];

    // Fill the rest
    for (let i = password.length; i < length; i++) {
      password += charset[Math.floor(Math.random() * charset.length)];
    }

    // Shuffle the password
    password = password.split('').sort(() => Math.random() - 0.5).join('');
    setGeneratedPassword(password);
  };

  const handleSendInvite = async () => {
    // Validation
    if (!inviteEmail) {
      setErrorMessage('Email address is required');
      return;
    }

    if (!generatedPassword) {
      setErrorMessage('Please generate a password first');
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(inviteEmail)) {
      setErrorMessage('Please enter a valid email address');
      return;
    }

    // Check if email already exists
    if (teamMembers.some(member => member.email.toLowerCase() === inviteEmail.toLowerCase())) {
      setErrorMessage('This email is already a team member');
      return;
    }

    // Check team limit (max 5 members including owner)
    if (teamMembers.length >= 5) {
      setErrorMessage('Maximum team size reached (5 members)');
      return;
    }

    try {
      setSending(true);
      setErrorMessage('');
      console.log('📧 Sending invite to:', inviteEmail);

      const response = await apiClient.post('/team/invite', {
        email: inviteEmail,
        temporaryPassword: generatedPassword
      });

      if (response.data.success) {
        setSuccessMessage(`Invitation sent to ${inviteEmail}. Temporary password has been emailed.`);
        setInviteEmail('');
        setGeneratedPassword('');
        setShowInviteForm(false);
        setTimeout(() => setSuccessMessage(''), 5000);

        // Reload team members
        loadTeamMembers();
      }

    } catch (error) {
      console.error('❌ Error sending invite:', error);
      setErrorMessage(error.response?.data?.error || 'Failed to send invitation. Please try again.');
    } finally {
      setSending(false);
    }
  };

  const handleRemoveMember = async (memberId) => {
    if (!window.confirm('Are you sure you want to remove this team member?')) {
      return;
    }

    try {
      console.log('🗑️ Removing member:', memberId);

      const response = await apiClient.delete(`/team/members/${memberId}`);

      if (response.data.success) {
        setSuccessMessage('Team member removed');
        setTimeout(() => setSuccessMessage(''), 3000);
        loadTeamMembers();
      }

    } catch (error) {
      console.error('❌ Error removing member:', error);
      setErrorMessage(error.response?.data?.error || 'Failed to remove team member');
    }
  };

  const getRoleBadge = (member) => {
    if (member.isOwner) {
      return (
        <span className="px-2.5 py-1 text-xs font-semibold rounded bg-purple-100 text-purple-700 border border-purple-200">
          <i className="fas fa-crown mr-1"></i>
          Owner
        </span>
      );
    }
    return (
      <span className="px-2.5 py-1 text-xs font-semibold rounded bg-blue-100 text-blue-700 border border-blue-200">
        <i className="fas fa-user mr-1"></i>
        Team Member
      </span>
    );
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'active':
        return (
          <span className="inline-flex items-center space-x-1 px-2.5 py-1 bg-green-100 text-green-800 text-xs font-semibold rounded border border-green-200">
            <i className="fas fa-check-circle"></i>
            <span>Active</span>
          </span>
        );
      case 'pending':
        return (
          <span className="inline-flex items-center space-x-1 px-2.5 py-1 bg-yellow-100 text-yellow-800 text-xs font-semibold rounded border border-yellow-200">
            <i className="fas fa-clock"></i>
            <span>Pending</span>
          </span>
        );
      default:
        return null;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <i className="fas fa-spinner fa-spin text-3xl text-blue-600 mb-3"></i>
          <p className="text-gray-600">Loading team members...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6 max-w-5xl">

      {/* Success Message */}
      {successMessage && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-center space-x-3">
          <i className="fas fa-check-circle text-green-600 text-lg"></i>
          <span className="text-green-800 text-sm font-medium">{successMessage}</span>
        </div>
      )}

      {/* Error Message */}
      {errorMessage && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center space-x-3">
          <i className="fas fa-exclamation-circle text-red-600 text-lg"></i>
          <span className="text-red-800 text-sm font-medium">{errorMessage}</span>
        </div>
      )}

      {/* Header */}
      <div className="bg-white border border-gray-300 rounded-lg shadow-sm overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-blue-600 rounded-lg flex items-center justify-center">
                <i className="fas fa-users text-white"></i>
              </div>
              <div>
                <h2 className="text-base font-bold text-gray-900">Team Members</h2>
                <p className="text-xs text-gray-600">
                  {teamMembers.length} {teamMembers.length === 1 ? 'member' : 'members'}
                </p>
              </div>
            </div>

            {/* Only show invite button for owner */}
            {currentUserIsOwner && (
              <button
                onClick={() => setShowInviteForm(!showInviteForm)}
                className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors flex items-center space-x-2"
              >
                <i className="fas fa-user-plus"></i>
                <span>Invite Member</span>
              </button>
            )}
          </div>
        </div>

        {/* Invite Form */}
        {showInviteForm && (
          <div className="p-6 bg-blue-50 border-b border-blue-200">
            <h3 className="text-sm font-bold text-gray-900 mb-3">Invite Admin Member</h3>
            <p className="text-xs text-gray-600 mb-4">
              All team members have admin access. Maximum 5 members total.
            </p>

            <div className="space-y-4">
              {/* Email */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Email Address <span className="text-red-500">*</span>
                </label>
                <input
                  type="email"
                  value={inviteEmail}
                  onChange={(e) => setInviteEmail(e.target.value)}
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="colleague@email.com"
                />
              </div>

              {/* Temporary Password */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Temporary Password <span className="text-red-500">*</span>
                </label>
                <div className="flex space-x-2">
                  <input
                    type="text"
                    value={generatedPassword}
                    readOnly
                    className="flex-1 px-4 py-2.5 border border-gray-300 rounded-lg bg-gray-50 font-mono text-sm"
                    placeholder="Click 'Generate Password' button"
                  />
                  <button
                    onClick={generatePassword}
                    className="px-4 py-2.5 bg-green-600 text-white text-sm font-medium rounded-lg hover:bg-green-700 transition-colors flex items-center space-x-2"
                  >
                    <i className="fas fa-key"></i>
                    <span>Generate</span>
                  </button>
                </div>
                <p className="text-xs text-gray-500 mt-2">
                  <i className="fas fa-info-circle mr-1"></i>
                  This temporary password will be emailed to the user. They must change it on first login.
                </p>
              </div>

              {/* Action Buttons */}
              <div className="flex space-x-3 pt-2">
                <button
                  onClick={handleSendInvite}
                  disabled={sending || !inviteEmail || !generatedPassword}
                  className="flex-1 px-4 py-2.5 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
                >
                  {sending ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      <span>Sending...</span>
                    </>
                  ) : (
                    <>
                      <i className="fas fa-paper-plane"></i>
                      <span>Send Invitation</span>
                    </>
                  )}
                </button>
                <button
                  onClick={() => {
                    setShowInviteForm(false);
                    setInviteEmail('');
                    setGeneratedPassword('');
                    setErrorMessage('');
                  }}
                  className="px-4 py-2.5 border border-gray-300 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Team Members List */}
        <div className="divide-y divide-gray-200">
          {teamMembers.map((member) => (
            <div
              key={member.id}
              className="p-6 hover:bg-gray-50 transition-colors"
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-4 flex-1">
                  {/* Avatar */}
                  <div className="w-12 h-12 bg-blue-600 rounded-full flex items-center justify-center flex-shrink-0">
                    <span className="text-white font-bold text-lg">
                      {member.name.charAt(0).toUpperCase()}
                    </span>
                  </div>

                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center space-x-2 mb-1">
                      <h3 className="text-sm font-semibold text-gray-900 truncate">
                        {member.name}
                        {member.email === localStorage.getItem('userEmail') && (
                          <span className="ml-2 text-xs text-gray-500">(You)</span>
                        )}
                      </h3>
                      {getStatusBadge(member.status)}
                    </div>
                    <p className="text-sm text-gray-600 truncate">{member.email}</p>
                    <p className="text-xs text-gray-500 mt-1">
                      {member.status === 'active'
                        ? `Joined ${new Date(member.joinedAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}`
                        : `Invited ${new Date(member.invitedAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}`
                      }
                    </p>
                  </div>
                </div>

                {/* Role & Actions */}
                <div className="flex items-center space-x-3 ml-4">
                  {getRoleBadge(member)}

                  {/* Only show delete button if current user is owner AND member is not owner */}
                  {currentUserIsOwner && !member.isOwner && (
                    <button
                      onClick={() => handleRemoveMember(member.id)}
                      className="text-red-600 hover:text-red-700"
                      title="Remove member"
                    >
                      <i className="fas fa-trash"></i>
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Info Box */}
      <div className="bg-gray-50 border border-gray-300 rounded-lg p-4">
        <div className="flex items-start space-x-3">
          <i className="fas fa-info-circle text-blue-600 text-lg mt-0.5"></i>
          <div className="text-sm text-gray-700">
            <p className="font-medium mb-2">Team Access Roles</p>
            <p className="text-sm text-gray-600 space-y-1">
              <strong className="text-purple-700"><i className="fas fa-crown mr-1"></i>Owner:</strong> Can invite and remove team members<br />
              <strong className="text-blue-700"><i className="fas fa-user mr-1"></i>Team Member:</strong> Full access to all platform features<br />
              <span className="text-gray-500 block mt-2">Maximum 5 members total. New members receive a temporary password via email.</span>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}